import { guid } from "../../helpers/string-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ListWidget from "../list/ListWidget/ListWidget.js";
import regionWidget from "../regionWidget.js";
import SearchFieldWidget from "../SearchFieldWidget/SearchFieldWidget.js";

/**
 * Renders groups of items with their group names. Only shows the group names if
 * there are several of them or if a single group has a name property.
 * A search field permits to reduce the number of relevant items.
 *
 * @param {{}[]} spec.groups - The different categories containing their specific items.
 * @param {function} [itemRenderer] - How each item should be displayed.
 * @param {function} [onSelectionChanged] - Called each time an item is selected.
 * @param {bool} [liveSearch] - Weither or not the search field is rendered.
 * @param {int} [liveSearchThreshold] - Number of items allowing to render the
 * search field.
 * @param {string} [cssClass] - If provided, this string is appended
 * to the `class` attribute of the main DOM node.
 */
export default class GroupedItemPicker extends Widget {
	constructor({
		groups,
		itemRenderer = (item) => item.label,
		onSelectionChanged = (_item) => {},
		liveSearch = true,
		liveSearchThreshold = 6,
		cssClass = "standalone",
	}) {
		super(...arguments);

		this._groups = groups;
		this._itemRenderer = itemRenderer;
		this._onSelectionChanged = onSelectionChanged;
		this._liveSearch = liveSearch;
		this._liveSearchThreshold = liveSearchThreshold;

		// The text the user typed in the search field:
		this._searchString = undefined;
		this._cssClass = cssClass;

		this._searchField = new SearchFieldWidget({
			inputId: guid(),
			inputClass: "form-control",
		});

		this._searchField.applySearchOn(this);

		this._groupListWidget = new ListWidget({
			itemRenderer: this._groupRenderer.bind(this),
			itemDataTestName: "group",
			itemCssClass: () => "group",
		});

		this._mainContentRegion = regionWidget({
			content: this._renderMainContentOn.bind(this),
		});

		this._setItemsInGroupListWidget();
	}

	applySearchFrom(searchField) {
		searchField.applySearchOnList(this);
	}

	updateSearch(searchString) {
		this._searchString = searchString;
		this._setItemsInGroupListWidget();
		this._mainContentRegion.update();
	}

	getSelected() {
		return this._allItems().find((item) => this.isSelected(item));
	}

	getValue() {
		return this.getSelected()?.value;
	}

	setGroups(groups) {
		this._groups = groups;
		this.update();
	}

	isSelected(item) {
		return !!item.selected;
	}

	selectValue(value) {
		this.selectMatching((item) => item.value === value);
	}

	selectMatching(predicate) {
		const item = this._allItems().find(predicate);

		if (item) this.select(item);
	}

	select(item) {
		this._allItems().forEach((element) => {
			element.selected = false;
		});

		item.selected = true;

		this._onSelectionChanged(item);

		this._updateActiveDOMItem();
	}

	renderContentOn(html) {
		html.div(
			{ class: ["item-picker", this._cssClass] },
			this._renderSearchFieldOn.bind(this),
			this._mainContentRegion,
		);
	}

	_renderMainContentOn(html) {
		this._shouldShowGroups()
			? this._renderGroupsOn(html)
			: this._renderFirstGroupOnlyOn(html);
	}

	_renderSearchFieldOn(html) {
		if (!this._shouldShowLiveSearch()) {
			return;
		}

		html.div({ klass: "search-field" }, this._searchField);
	}

	_renderGroupsOn(html) {
		html.render(this._groupListWidget);
	}

	_renderFirstGroupOnlyOn(html) {
		html.render(this._groupItemsRenderer(this._groups[0]));
	}

	_groupRenderer(group) {
		let renderers = [this._groupItemsRenderer(group)];

		if (group.name) {
			renderers = [this._groupNameRenderer(group), ...renderers];
		}

		return renderers;
	}

	_groupNameRenderer(group) {
		return (html) =>
			html.div(
				{ class: "group-name", "data-test": "group-name" },
				group.name,
			);
	}

	_groupItemsRenderer(group) {
		return new ListWidget({
			items: this._filterVisibleItems(group.items),
			itemRenderer: (item) => (html) => html.a(this._itemRenderer(item)),
			cssClass: "items",
			onItemClicked: (item) => this.select(item),
			itemCssClass: (item) => `item ${item.selected ? "active" : ""}`,
		});
	}

	_shouldShowGroups() {
		if (this._groups.length > 1) {
			return true;
		}

		return this._groups.length === 1 && this._groups[0].name;
	}

	_setItemsInGroupListWidget() {
		this._groupListWidget.setItems(this._groups);
	}

	_filterVisibleItems(items) {
		return items.filter((item) => this._shouldRenderItem(item));
	}

	_shouldRenderItem(item) {
		return (
			!this._searchString || !item.match || item.match(this._searchString)
		);
	}

	_allItems() {
		return this._groups.flatMap((group) => group.items);
	}

	/**
	 * Update the active item in the DOM without triggering a full refresh of
	 * the widget, which would reset the scrolling to the top.
	 */
	_updateActiveDOMItem() {
		this.asJQuery()
			.find(".item.active")
			.get()
			.forEach((node) => node.classList.remove("active"));

		const index = this._getSelectedVisibleIndex();

		if (index !== -1) {
			const selected = this.asJQuery().find(".item").get()[index];

			selected && selected.classList.add("active");
		}
	}

	/**
	 * Return the index of the selected item in the visible items.
	 *
	 * If no item is selected, or if the selected item is not visible,
	 * return -1.
	 */
	_getSelectedVisibleIndex() {
		const selected = this.getSelected();

		if (!selected) {
			return -1;
		}

		return this._filterVisibleItems(this._allItems()).indexOf(selected);
	}

	_shouldShowLiveSearch() {
		return (
			this._liveSearch &&
			this._allItems().length >= this._liveSearchThreshold
		);
	}
}
